import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { 
  X, Plus, UserPlus, Users, Search, Loader2, Upload, 
  FileText, AlertCircle, CheckCircle, FileDown 
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const availableTeams = [
  { name: 'Engineering', color: 'blue' },
  { name: 'Design', color: 'purple' },
  { name: 'Marketing', color: 'green' },
  { name: 'Sales', color: 'orange' },
  { name: 'Support', color: 'yellow' },
  { name: 'Operations', color: 'pink' },
  { name: 'Finance', color: 'red' },
  { name: 'Legal', color: 'indigo' },
  { name: 'HR', color: 'teal' },
  { name: 'Product', color: 'cyan' },
];

const roleTypes = [
  { value: 'Member', label: 'Member', description: 'Standard team member' },
  { value: 'Manager', label: 'Manager', description: 'Team or department manager' },
  { value: 'Owner', label: 'Owner', description: 'Full administrative access' },
];

// CSV Template headers
const CSV_HEADERS = [
  'name',
  'email',
  'username',
  'job_title',
  'role',
  'department',
  'status'
];

// Generate CSV template
const generateCSVTemplate = () => {
  const headers = CSV_HEADERS.join(',');
  const sampleRows = [
    'John Doe,john.doe@example.com,jdoe,Site Supervisor,Member,Operations,active',
    'Jane Smith,jane.smith@example.com,jsmith,HR Manager,Manager,HR,active',
    'Mike Johnson,mike.j@example.com,mjohnson,IT Director,Owner,IT,active',
    ',,,,,,',
    '# IMPORTANT: Column order is: name,email,username,job_title,role,department,status',
    '# - name: Full name (REQUIRED)',
    '# - email: Email address (REQUIRED)',
    '# - username: Optional username (auto-generated if empty)',
    '# - job_title: Job title like "Site Supervisor" or "HR Manager" (optional)',
    '# - role: Permission level - must be Member, Manager, or Owner (defaults to Member)',
    '# - department: Department like "Operations" or "HR" (optional)',
    '# - status: active or inactive (defaults to active)',
  ];

  return headers + '\n' + sampleRows.join('\n');
};

// Parse CSV content
const parseCSV = (content) => {
  const lines = content.split('\n').filter(line => line.trim() && !line.startsWith('#'));
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

  console.log('CSV Headers:', headers);

  const data = [];
  const errors = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim() || line.startsWith('#')) continue;

    const values = line.split(',').map(v => v.trim());

    if (values.length !== headers.length) {
      errors.push(`Row ${i + 1}: Column count mismatch (expected ${headers.length}, got ${values.length})`);
      continue;
    }

    const row = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });

    // Validate required fields
    if (!row.email) {
      errors.push(`Row ${i + 1}: Email is required`);
      continue;
    }

    if (!row.name) {
      errors.push(`Row ${i + 1}: Name is required`);
      continue;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(row.email)) {
      errors.push(`Row ${i + 1}: Invalid email format (${row.email})`);
      continue;
    }

    // Set defaults
    row.status = row.status || 'active';
    row.role = row.role || 'Member';

    console.log(`Row ${i} parsed:`, {
      name: row.name,
      email: row.email,
      username: row.username,
      job_title: row.job_title,
      role: row.role,
      department: row.department,
      status: row.status
    });

    data.push(row);
  }

  console.log('Total rows parsed:', data.length);

  return { data, errors };
};

export const AddMemberDialog = ({ open, onOpenChange, onAddMember, projectId, isStackView = false, stackProjects = [] }) => {
  const [activeTab, setActiveTab] = useState('new');
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    job_title: '',
    role: 'Member',
    department: '',
  });

  // Update selectedProjectId when projectId prop changes
  useEffect(() => {
    if (projectId) {
      setSelectedProjectId(projectId);
    } else if (isStackView && stackProjects && stackProjects.length > 0) {
      // Default to first project in stack
      setSelectedProjectId(stackProjects[0].id);
    }
  }, [projectId, isStackView, stackProjects]);
  
  const [userEditedUsername, setUserEditedUsername] = useState(false);
  const [companyMembers, setCompanyMembers] = useState([]);
  const [selectedCompanyMembers, setSelectedCompanyMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingCompanyMembers, setLoadingCompanyMembers] = useState(false);
  
  // Import CSV state
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch company members when dialog opens or selectedProjectId changes
  useEffect(() => {
    if (open && activeTab === 'existing') {
      fetchCompanyMembers();
    }
  }, [open, activeTab, selectedProjectId]);

  const fetchCompanyMembers = async () => {
    setLoadingCompanyMembers(true);
    try {
      // Validate selectedProjectId
      if (!selectedProjectId || selectedProjectId === 'all') {
        // For "all" selection, we need to fetch without excluding
        if (selectedProjectId === 'all' && stackProjects?.length > 0) {
          // Use first project to get company members
          const { data, error } = await supabase.rpc('get_company_members_for_dialog', {
            p_project_id: stackProjects[0].id,
            p_exclude_existing: false
          });

          if (error) {
            console.error('Error fetching company members:', error);
            setCompanyMembers([]);
            return;
          }

          const members = (data?.members || []).map(m => ({
            ...m,
            project_id: null
          }));
          setCompanyMembers(members);
        } else {
          setCompanyMembers([]);
        }
        setLoadingCompanyMembers(false);
        return;
      }

      // Use optimized RPC (replaces 5 sequential queries with 1)
      const { data, error } = await supabase.rpc('get_company_members_for_dialog', {
        p_project_id: selectedProjectId,
        p_exclude_existing: true
      });

      if (error) {
        console.error('Error fetching company members:', error);
        toast.error('Failed to load company members');
        setCompanyMembers([]);
        return;
      }

      if (data?.error) {
        console.error('RPC error:', data.error);
        setCompanyMembers([]);
        return;
      }

      const members = (data?.members || []).map(m => ({
        ...m,
        project_id: null
      }));
      setCompanyMembers(members);
    } catch (error) {
      console.error('Error fetching company members:', error);
      toast.error('Failed to load company members');
      setCompanyMembers([]);
    } finally {
      setLoadingCompanyMembers(false);
    }
  };

  // Handle CSV file selection
  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.csv')) {
      setErrors(['Please select a CSV file']);
      return;
    }
    
    setFile(selectedFile);
    setErrors([]);
    setParsing(true);
    
    try {
      const content = await selectedFile.text();
      const { data, errors: parseErrors } = parseCSV(content);
      
      if (parseErrors.length > 0) {
        setErrors(parseErrors);
        setPreview(null);
      } else {
        setPreview(data);
        setErrors([]);
      }
    } catch (error) {
      setErrors(['Failed to read file']);
      setPreview(null);
    } finally {
      setParsing(false);
    }
  };

  // Handle CSV import
  const handleImport = async () => {
    if (!preview || preview.length === 0) return;

    // Validate selectedProjectId
    if (!selectedProjectId) {
      toast.error('Please select a space');
      return;
    }

    setImporting(true);
    setImportProgress(0);

    // Determine which projects to add to
    const projectIdsToAddTo = selectedProjectId === 'all'
      ? stackProjects.map(p => p.id)
      : [selectedProjectId];

    // Get company_id from the first project
    const { data: projectData, error: projectError } = await supabase
      .from('projects')
      .select('company_id')
      .eq('id', projectIdsToAddTo[0])
      .single();

    if (projectError || !projectData) {
      toast.error('Failed to get project details');
      setImporting(false);
      return;
    }

    const companyId = projectData.company_id;

    const results = { success: 0, failed: 0, errors: [] };
    const totalOperations = preview.length * projectIdsToAddTo.length;
    let completedOperations = 0;

    // Create user_profile map to avoid duplicate creation
    const userProfileMap = new Map();

    for (const member of preview) {
      try {
        // Step 1: Create or get user_profile (once per email)
        let userId = userProfileMap.get(member.email);

        if (!userId) {
          const { data: existingProfiles, error: checkError } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('email', member.email)
            .limit(1);

          if (checkError) {
            results.failed++;
            results.errors.push(`${member.email}: Failed to check profile - ${checkError.message}`);
            continue;
          }

          if (existingProfiles && existingProfiles.length > 0) {
            userId = existingProfiles[0].id;

            // Update existing profile with new job_title/department if provided
            const updateData = {};
            if (member.job_title && member.job_title.trim() !== '') {
              updateData.job_title = member.job_title.trim();
            }
            if (member.department && member.department.trim() !== '') {
              updateData.department = member.department.trim();
            }

            if (Object.keys(updateData).length > 0) {
              console.log('Updating existing user_profile:', { userId, ...updateData });
              await supabase
                .from('user_profiles')
                .update(updateData)
                .eq('id', userId);
            }
          } else {
            // Parse name
            const nameParts = member.name.split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            const profileData = {
              email: member.email,
              first_name: firstName,
              last_name: lastName,
              job_title: member.job_title && member.job_title.trim() !== '' ? member.job_title.trim() : null,
              department: member.department && member.department.trim() !== '' ? member.department.trim() : null,
              company_id: companyId,
              created_date: new Date().toISOString()
            };

            console.log('Creating user_profile:', profileData);

            const { data: newProfile, error: profileError } = await supabase
              .from('user_profiles')
              .insert(profileData)
              .select('id')
              .single();

            if (profileError) {
              results.failed++;
              results.errors.push(`${member.email}: Failed to create profile - ${profileError.message}`);
              continue;
            }

            userId = newProfile.id;

            // Create company_member
            await supabase
              .from('company_members')
              .insert({
                user_id: userId,
                company_id: companyId,
                role: 'User',
                joined_date: new Date().toISOString()
              });
          }

          userProfileMap.set(member.email, userId);
        }

        // Step 2: Add to each selected project
        for (const pid of projectIdsToAddTo) {
          // Check if space_member already exists
          const { data: existing } = await supabase
            .from('space_members')
            .select('id')
            .eq('project_id', pid)
            .eq('user_id', userId)
            .single();

          if (existing) {
            results.failed++;
            results.errors.push(`${member.email} already exists in this space`);
          } else {
            const spaceMemberData = {
              project_id: pid,
              user_id: userId,
              role: member.role && member.role.trim() !== '' ? member.role.trim() : 'Member',
              status: member.status && member.status.trim() !== '' ? member.status.trim() : 'active',
              last_active: new Date().toISOString(),
              sites: 1,
              show_on_matrix: true,
              created_date: new Date().toISOString()
            };

            console.log('Creating space_member:', spaceMemberData);

            const { error } = await supabase
              .from('space_members')
              .insert(spaceMemberData);

            if (error) {
              results.failed++;
              results.errors.push(`${member.email}: ${error.message}`);
            } else {
              results.success++;
            }
          }

          completedOperations++;
          setImportProgress((completedOperations / totalOperations) * 100);
        }
      } catch (error) {
        results.failed++;
        results.errors.push(`${member.email}: ${error.message}`);
      }
    }

    setImportResults(results);
    setImporting(false);

    if (results.success > 0) {
      if (selectedProjectId === 'all') {
        toast.success(`Imported members to ${projectIdsToAddTo.length} space(s)`);
      } else {
        toast.success(`Imported ${results.success} member(s)`);
      }
      onAddMember(null); // Trigger refresh

      // Reset after success
      setTimeout(() => {
        setFile(null);
        setPreview(null);
        setImportResults(null);
        setImportProgress(0);
        if (results.failed === 0) {
          onOpenChange(false);
        }
      }, 2000);
    }
  };

  // Download CSV template
  const downloadTemplate = () => {
    const csv = generateCSVTemplate();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'team_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleInputChange = (field, value) => {
    if (field === 'username') {
      // User is manually editing username
      setUserEditedUsername(true);
      setFormData(prev => ({
        ...prev,
        username: value,
      }));
    } else if (field === 'name') {
      // Update name and auto-generate username if user hasn't manually edited it
      const newData = {
        ...formData,
        name: value,
      };
      
      if (!userEditedUsername) {
        // Auto-generate username from name
        const username = value.trim().toLowerCase()
          .replace(/[^a-z0-9\s]/g, '') // Remove special characters
          .replace(/\s+/g, '.'); // Replace spaces with dots
        newData.username = username;
      }
      
      setFormData(newData);
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };


  const handleAddExistingMembers = async () => {
    if (selectedCompanyMembers.length === 0) {
      toast.error('Please select at least one member to add');
      return;
    }

    // Validate selectedProjectId
    if (!selectedProjectId) {
      toast.error('Please select a space');
      return;
    }

    setSubmitting(true);
    try {
      // Determine which projects to add to
      const projectIdsToAddTo = selectedProjectId === 'all'
        ? stackProjects.map(p => p.id)
        : [selectedProjectId];

      let totalAdded = 0;
      let totalSkipped = 0;
      const addedMembers = [];

      // Process each selected member
      for (const memberId of selectedCompanyMembers) {
        const member = companyMembers.find(m => m.id === memberId);
        if (!member) continue;

        // Add to each selected project
        for (const pid of projectIdsToAddTo) {
          // Check if this member already exists in this specific project
          const { data: existingInProject } = await supabase
            .from('space_members')
            .select('id')
            .eq('user_id', member.user_id)
            .eq('project_id', pid)
            .single();

          if (existingInProject) {
            // Already in this project
            totalSkipped++;
            continue;
          }

          // Create space_member for this project
          // Ensure role is valid for space_members (Owner, Manager, Member, Viewer)
          const validSpaceRoles = ['Owner', 'Manager', 'Member', 'Viewer'];
          let spaceRole = member.role || 'Member';
          if (!validSpaceRoles.includes(spaceRole)) {
            // Map company roles to space roles
            if (spaceRole === 'Admin') spaceRole = 'Owner';
            else spaceRole = 'Member';
          }

          const { data: newSpaceMember, error: insertError } = await supabase
            .from('space_members')
            .insert({
              project_id: pid,
              user_id: member.user_id,
              role: spaceRole,
              status: 'active',
              last_active: new Date().toISOString(),
              sites: 1,
              show_on_matrix: true,
              created_date: new Date().toISOString()
            })
            .select()
            .single();

          if (insertError) {
            console.error(`Error adding member ${member.email}:`, insertError);
            totalSkipped++;
          } else if (newSpaceMember) {
            totalAdded++;
            addedMembers.push({
              id: newSpaceMember.id,
              name: member.name,
              email: member.email,
              username: '',
              role: member.role || 'Member',
              department: newSpaceMember.department || '',
              status: 'active',
              compliance: 0,
              lastActive: 'Just now',
              avatar_url: member.avatar_url || null,
              teams: []
            });
          }
        }
      }

      // Show success message
      if (totalAdded > 0) {
        if (selectedProjectId === 'all') {
          toast.success(`Added ${totalAdded} member(s) to all spaces`);
        } else {
          toast.success(`Added ${totalAdded} member(s) to space`);
        }

        onAddMember(addedMembers[addedMembers.length - 1]); // Trigger refresh

        // Reset selection
        setSelectedCompanyMembers([]);
        onOpenChange(false);
      } else if (totalSkipped > 0) {
        toast.error('Selected members already exist in the selected space(s)');
      }
    } catch (error) {
      console.error('Error adding existing members:', error);
      toast.error('Failed to add members');
    } finally {
      setSubmitting(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate selectedProjectId
    if (!selectedProjectId) {
      toast.error('Please select a space');
      return;
    }

    setSubmitting(true);
    try {
      // Determine which projects to add to
      const projectIdsToAddTo = selectedProjectId === 'all'
        ? stackProjects.map(p => p.id)
        : [selectedProjectId];

      // Get company_id from the first project (all projects in stack should have same company)
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('company_id')
        .eq('id', projectIdsToAddTo[0])
        .single();

      if (projectError || !project) {
        toast.error('Failed to get project details');
        return;
      }

      const companyId = project.company_id;

      let addedCount = 0;
      let skippedCount = 0;
      let lastAddedMember = null;

      // Step 1: Create or get user_profile
      let userId = null;
      const hasEmail = formData.email && formData.email.trim() !== '';

      // Check if user_profile already exists (only if email provided)
      if (hasEmail) {
        const { data: existingProfile } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('email', formData.email)
          .eq('company_id', companyId)
          .single();

        if (existingProfile) {
          userId = existingProfile.id;

          // Update existing profile with new job_title/department if provided
          const updateData = {};
          if (formData.job_title && formData.job_title.trim() !== '') {
            updateData.job_title = formData.job_title.trim();
          }
          if (formData.department && formData.department.trim() !== '') {
            updateData.department = formData.department.trim();
          }

          if (Object.keys(updateData).length > 0) {
            await supabase
              .from('user_profiles')
              .update(updateData)
              .eq('id', userId);
          }
        }
      }

      if (!userId) {
        // Parse name into first/last
        const nameParts = formData.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        // Create new user_profile (null email if not provided)
        const { data: newProfile, error: profileError } = await supabase
          .from('user_profiles')
          .insert({
            email: hasEmail ? formData.email.trim() : null,
            first_name: firstName,
            last_name: lastName,
            job_title: formData.job_title && formData.job_title.trim() !== '' ? formData.job_title.trim() : null,
            department: formData.department && formData.department.trim() !== '' ? formData.department.trim() : null,
            company_id: companyId,
            created_date: new Date().toISOString()
          })
          .select('id')
          .single();

        if (profileError) {
          console.error('Error creating user profile:', profileError);
          toast.error('Failed to create user profile');
          return;
        }

        userId = newProfile.id;

        // Step 2: Create company_member if it doesn't exist
        const { error: companyMemberError } = await supabase
          .from('company_members')
          .insert({
            user_id: userId,
            company_id: companyId,
            role: 'User',
            joined_date: new Date().toISOString()
          });

        if (companyMemberError && !companyMemberError.message?.includes('duplicate')) {
          console.error('Error creating company member:', companyMemberError);
        }
      }

      // Step 3: Add to each selected project as space_member
      for (const pid of projectIdsToAddTo) {
        // Try to insert, catch duplicate errors (RLS policy prevents checking existence)
        const { data: newSpaceMember, error: spaceMemberError } = await supabase
          .from('space_members')
          .insert({
            project_id: pid,
            user_id: userId,
            role: formData.role && formData.role.trim() !== '' ? formData.role.trim() : 'Member',
            status: 'active',
            last_active: new Date().toISOString(),
            sites: 1,
            show_on_matrix: true,
            created_date: new Date().toISOString()
          })
          .select()
          .single();

        if (spaceMemberError) {
          // Check if it's a duplicate key violation (already exists)
          if (spaceMemberError.code === '23505') {
            // User already exists in this space, count as skipped
            skippedCount++;
          } else {
            // Other error, log and skip
            console.error('Error adding space member:', spaceMemberError);
            skippedCount++;
          }
        } else if (newSpaceMember) {
          addedCount++;
          lastAddedMember = {
            id: newSpaceMember.id,
            name: formData.name,
            email: formData.email,
            username: formData.username || '',
            role: formData.role,
            department: formData.department || '',
            status: 'active',
            compliance: 0,
            lastActive: 'Just now',
            avatar_url: null,
            teams: []
          };
        }
      }

      // Update the UI and show appropriate message
      if (addedCount > 0) {
        onAddMember(lastAddedMember); // Trigger refresh

        if (selectedProjectId === 'all') {
          if (skippedCount > 0) {
            toast.success(`Added to ${addedCount} space(s). Already exists in ${skippedCount} space(s).`);
          } else {
            toast.success(`Team member added to all ${addedCount} spaces successfully`);
          }
        } else {
          toast.success('Team member added successfully');
        }

        // Reset form
        setFormData({
          name: '',
          email: '',
          username: '',
          job_title: '',
          role: 'Member',
          department: '',
        });
        setUserEditedUsername(false);

        // Close dialog
        onOpenChange(false);
      } else {
        toast.error('Member already exists in all selected spaces');
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast.error('An unexpected error occurred');
      // Make sure dialog closes even on error
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  const getTeamBadgeColor = (color) => {
    const colorMap = {
      blue: "bg-blue-100 text-blue-700",
      green: "bg-green-100 text-green-700",
      purple: "bg-purple-100 text-purple-700",
      orange: "bg-orange-100 text-orange-700",
      yellow: "bg-yellow-100 text-yellow-700",
      pink: "bg-pink-100 text-pink-700",
      red: "bg-red-100 text-red-700",
      indigo: "bg-indigo-100 text-indigo-700",
      teal: "bg-teal-100 text-teal-700",
      cyan: "bg-cyan-100 text-cyan-700",
    };
    return colorMap[color] || "bg-gray-100 text-gray-700";
  };

  const filteredCompanyMembers = companyMembers.filter(member =>
    member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen) {
        // Reset state when closing
        setActiveTab('new');
        setSelectedCompanyMembers([]);
        setSearchQuery('');
        setFormData({
          name: '',
          email: '',
          username: '',
          job_title: '',
          role: 'Member',
          department: '',
        });
        setUserEditedUsername(false);
      }
      onOpenChange(newOpen);
    }}>
      <DialogContent className={cn(
        "transition-all duration-300 ease-in-out",
        preview && activeTab === 'import' ? "sm:max-w-6xl" : "sm:max-w-[600px]"
      )}>
        <DialogHeader>
          <DialogTitle>Add Team Members</DialogTitle>
          <DialogDescription>
            Add new team members or select existing company members to add to this project.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="new" className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              New
            </TabsTrigger>
            <TabsTrigger value="existing" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Company
            </TabsTrigger>
            <TabsTrigger value="import" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Import
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="new" className="mt-4">
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="john.smith@example.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </div>

            {/* Space Selector - only shown in stack view */}
            {isStackView && stackProjects && stackProjects.length > 0 && (
              <div className="grid gap-2">
                <Label htmlFor="space">Space *</Label>
                <Select
                  value={selectedProjectId}
                  onValueChange={setSelectedProjectId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a space" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Spaces ({stackProjects.length})</SelectItem>
                    {stackProjects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                placeholder="john.smith"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="job_title">Job Title</Label>
              <Input
                id="job_title"
                placeholder="e.g., Site Supervisor, HR Manager"
                value={formData.job_title}
                onChange={(e) => handleInputChange('job_title', e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="role">Role *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => handleInputChange('role', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roleTypes.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div>
                        <div className="font-medium">{role.label}</div>
                        <div className="text-xs text-muted-foreground">{role.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="department">Department</Label>
              <Input
                id="department"
                placeholder="e.g., Warehouse, Operations, Engineering"
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Enter the department or team this member belongs to
              </p>
            </div>

              </div>

              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</> : 'Add Member'}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>
          
          <TabsContent value="existing" className="mt-4">
            <div className="space-y-4">
              {/* Space Selector - only shown in stack view */}
              {isStackView && stackProjects && stackProjects.length > 0 && (
                <div className="grid gap-2">
                  <Label htmlFor="existing-space">Space *</Label>
                  <Select
                    value={selectedProjectId}
                    onValueChange={setSelectedProjectId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a space" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Spaces ({stackProjects.length})</SelectItem>
                      {stackProjects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Search bar and select all */}
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                
                {/* Select All checkbox */}
                {filteredCompanyMembers.length > 0 && (
                  <div className="flex items-center space-x-2 px-1">
                    <Checkbox
                      checked={
                        filteredCompanyMembers.length > 0 &&
                        filteredCompanyMembers.every(member => 
                          selectedCompanyMembers.includes(member.id)
                        )
                      }
                      onCheckedChange={(checked) => {
                        if (checked) {
                          // Select all filtered members
                          const allFilteredIds = filteredCompanyMembers.map(m => m.id);
                          const newSelection = [...new Set([...selectedCompanyMembers, ...allFilteredIds])];
                          setSelectedCompanyMembers(newSelection);
                        } else {
                          // Deselect all filtered members
                          const filteredIds = filteredCompanyMembers.map(m => m.id);
                          setSelectedCompanyMembers(
                            selectedCompanyMembers.filter(id => !filteredIds.includes(id))
                          );
                        }
                      }}
                    />
                    <label className="text-sm font-medium">
                      Select all {searchQuery ? 'filtered' : ''} ({filteredCompanyMembers.length})
                    </label>
                  </div>
                )}
              </div>
              
              {/* Member list */}
              <ScrollArea className="h-[300px] w-full rounded-md border p-4">
                {loadingCompanyMembers ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredCompanyMembers.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    {searchQuery ? 'No members found matching your search' : 'No available company members to add'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredCompanyMembers.map((member) => (
                      <div
                        key={member.id}
                        className={cn(
                          "flex items-center space-x-3 p-3 rounded-lg border transition-colors",
                          selectedCompanyMembers.includes(member.id) && "bg-accent"
                        )}
                      >
                        <Checkbox
                          checked={selectedCompanyMembers.includes(member.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedCompanyMembers([...selectedCompanyMembers, member.id]);
                            } else {
                              setSelectedCompanyMembers(selectedCompanyMembers.filter(id => id !== member.id));
                            }
                          }}
                        />
                        {member.avatar_url ? (
                          <img
                            src={member.avatar_url}
                            alt={member.name}
                            className="h-10 w-10 rounded-full"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                            {member.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{member.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              
              {/* Selected count */}
              {selectedCompanyMembers.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  {selectedCompanyMembers.length} member(s) selected
                </div>
              )}
            </div>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={handleAddExistingMembers}
                disabled={selectedCompanyMembers.length === 0 || loadingCompanyMembers || submitting}
              >
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</> : `Add ${selectedCompanyMembers.length > 0 ? `${selectedCompanyMembers.length} ` : ''}Member${selectedCompanyMembers.length !== 1 ? 's' : ''}`}
              </Button>
            </DialogFooter>
          </TabsContent>
          
          <TabsContent value="import" className="mt-4">
            <div className="space-y-4">
              {/* Space Selector - only shown in stack view */}
              {isStackView && stackProjects && stackProjects.length > 0 && (
                <div className="grid gap-2">
                  <Label htmlFor="import-space">Space *</Label>
                  <Select
                    value={selectedProjectId}
                    onValueChange={setSelectedProjectId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a space" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Spaces ({stackProjects.length})</SelectItem>
                      {stackProjects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* File upload area */}
              {!file && !importResults && (
                <div className="space-y-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={downloadTemplate}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    Download CSV Template
                  </Button>
                  
                  <div
                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-sm font-medium mb-1">Click to upload CSV file</p>
                    <p className="text-xs text-muted-foreground">or drag and drop</p>
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              )}
              
              {/* Parsing indicator */}
              {parsing && (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Reading file...</p>
                </div>
              )}
              
              {/* Errors */}
              {errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-1">
                      {errors.slice(0, 5).map((error, idx) => (
                        <div key={idx} className="text-xs">{error}</div>
                      ))}
                      {errors.length > 5 && (
                        <div className="text-xs font-medium">...and {errors.length - 5} more errors</div>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Preview */}
              {preview && !importing && !importResults && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm font-medium">{file?.name}</span>
                      <Badge variant="secondary">{preview.length} members</Badge>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFile(null);
                        setPreview(null);
                        setErrors([]);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="rounded-md border">
                    <div className="p-2 bg-muted/50 border-b">
                      <p className="text-xs font-medium text-muted-foreground">
                        Preview - Verify all columns are correct before importing
                      </p>
                    </div>
                    <ScrollArea className="h-[300px]">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/30 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-xs">#</th>
                              <th className="px-3 py-2 text-left font-medium text-xs">Name</th>
                              <th className="px-3 py-2 text-left font-medium text-xs">Email</th>
                              <th className="px-3 py-2 text-left font-medium text-xs">Username</th>
                              <th className="px-3 py-2 text-left font-medium text-xs">Job Title</th>
                              <th className="px-3 py-2 text-left font-medium text-xs">Role (Permission)</th>
                              <th className="px-3 py-2 text-left font-medium text-xs">Department</th>
                              <th className="px-3 py-2 text-left font-medium text-xs">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.map((member, idx) => (
                              <tr key={idx} className="border-b hover:bg-muted/20">
                                <td className="px-3 py-2 text-muted-foreground">{idx + 1}</td>
                                <td className="px-3 py-2 font-medium">{member.name || <span className="text-muted-foreground italic">empty</span>}</td>
                                <td className="px-3 py-2">{member.email || <span className="text-muted-foreground italic">empty</span>}</td>
                                <td className="px-3 py-2 text-muted-foreground">{member.username || <span className="text-muted-foreground italic">auto</span>}</td>
                                <td className="px-3 py-2">
                                  {member.job_title && member.job_title.trim() !== '' ? (
                                    <Badge variant="secondary" className="text-xs">{member.job_title}</Badge>
                                  ) : (
                                    <span className="text-muted-foreground italic text-xs">none</span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <Badge variant="outline" className="text-xs">
                                    {member.role && member.role.trim() !== '' ? member.role : 'Member'}
                                  </Badge>
                                </td>
                                <td className="px-3 py-2">
                                  {member.department && member.department.trim() !== '' ? (
                                    member.department
                                  ) : (
                                    <span className="text-muted-foreground italic text-xs">none</span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <Badge variant={member.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                                    {member.status || 'active'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </ScrollArea>
                  </div>

                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      <strong>Before importing, verify:</strong>
                      <ul className="list-disc list-inside mt-1 space-y-0.5">
                        <li><strong>Job Title</strong> = Actual position (e.g., "Site Supervisor", "HR Manager")</li>
                        <li><strong>Role (Permission)</strong> = Access level (Member/Manager/Owner)</li>
                        <li><strong>Department</strong> = Team/department name</li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                </div>
              )}
              
              {/* Import progress */}
              {importing && (
                <div className="space-y-3">
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2">Importing members...</p>
                    <Progress value={importProgress} className="w-full" />
                    <p className="text-xs text-muted-foreground mt-1">
                      {Math.round(importProgress)}% complete
                    </p>
                  </div>
                </div>
              )}
              
              {/* Import results */}
              {importResults && (
                <div className="space-y-3">
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      Successfully imported {importResults.success} member(s)
                      {importResults.failed > 0 && ` (${importResults.failed} failed)`}
                    </AlertDescription>
                  </Alert>
                  
                  {importResults.errors.length > 0 && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-1">
                          {importResults.errors.slice(0, 5).map((error, idx) => (
                            <div key={idx} className="text-xs">{error}</div>
                          ))}
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={importing}>
                Cancel
              </Button>
              {preview && !importing && !importResults && (
                <Button onClick={handleImport}>
                  Import {preview.length} Member{preview.length !== 1 ? 's' : ''}
                </Button>
              )}
              {importing && (
                <Button disabled>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing...
                </Button>
              )}
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};